import React, { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import { useSiteContext } from "../components/layout/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Pencil, Trash2, Building2, ClipboardList } from "lucide-react";
import { toast } from "sonner";

/**
 * Generic Asset Registry component used by all 6 registry routes.
 * Pass `assetType`, `title`, `icon`, and a `schema` describing fields.
 *
 * schema = [
 *   { key: "name", label: "Name", required: true, kind: "text", section: "common" },
 *   { key: "meta.zone", label: "Zone", kind: "select", options: [...], section: "type" },
 * ]
 */
export default function AssetRegistry({ assetType, title, icon: Icon, accentColor = "orange", schema }) {
  const { sites, selectedSite } = useSiteContext();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [gateways, setGateways] = useState([]);
  const [ioOptions, setIoOptions] = useState([]);
  const [liveValue, setLiveValue] = useState(null);
  const [points, setPoints] = useState([]);
  const emptyForm = useMemo(() => {
    const base = { name: "", location: "", location_label: "", system: "", make: "", model: "", status: "active", notes: "", meta: {}, gateway_id: "", io_point_name: "", linked_point_id: "" };
    // apply schema defaults
    (schema || []).forEach((f) => {
      if (f.default === undefined) return;
      if (f.key.startsWith("meta.")) base.meta[f.key.slice(5)] = f.default;
      else base[f.key] = f.default;
    });
    return base;
  }, [schema]);
  const [form, setForm] = useState(emptyForm);
  // Reset form to defaults whenever schema changes (e.g. switching between Tanks / Pumps registries)
  useEffect(() => { setForm(emptyForm); }, [emptyForm]);

  const load = () => {
    const params = { asset_type: assetType };
    if (selectedSite && selectedSite !== "all") params.site_id = selectedSite;
    api.get("/assets", { params }).then((r) => setItems(r.data));
  };
  useEffect(load, [selectedSite, assetType]);

  // Fetch gateways for the relevant site whenever the dialog opens
  useEffect(() => {
    if (!open) return;
    const site = editing ? editing.site_id : (selectedSite && selectedSite !== "all" ? selectedSite : sites[0]?.site_id);
    if (!site) { setGateways([]); return; }
    api.get("/hardware/gateways", { params: { site_id: site } }).then((r) => setGateways(r.data)).catch(() => setGateways([]));
  }, [open, editing, selectedSite, sites]);

  // Fetch IO mappings for the selected gateway
  useEffect(() => {
    if (!form.gateway_id) { setIoOptions([]); return; }
    api.get(`/hardware/gateways/${form.gateway_id}/io-mappings`).then((r) => setIoOptions(r.data)).catch(() => setIoOptions([]));
  }, [form.gateway_id]);

  // Fetch addressable panel points for fire alarm devices, so one can be linked for live status
  useEffect(() => {
    if (!open || assetType !== "fire_alarm_device") return;
    const site = editing ? editing.site_id : (selectedSite && selectedSite !== "all" ? selectedSite : sites[0]?.site_id);
    if (!site) { setPoints([]); return; }
    api.get("/addressable/points", { params: { site_id: site } }).then((r) => setPoints(r.data)).catch(() => setPoints([]));
  }, [open, editing, selectedSite, sites, assetType]);

  // Fetch the current live value preview (only possible once the asset exists)
  useEffect(() => {
    if (!editing || !form.gateway_id || !form.io_point_name) { setLiveValue(null); return; }
    api.get(`/assets/${editing.asset_id}/live-value`).then((r) => setLiveValue(r.data)).catch(() => setLiveValue(null));
  }, [editing, form.gateway_id, form.io_point_name]);

  const setVal = (key, val) => {
    if (key.startsWith("meta.")) {
      const mk = key.slice(5);
      setForm((f) => ({ ...f, meta: { ...(f.meta || {}), [mk]: val } }));
    } else {
      setForm((f) => ({ ...f, [key]: val }));
    }
  };
  const getVal = (key, src = form) => key.startsWith("meta.") ? (src.meta || {})[key.slice(5)] ?? "" : src[key] ?? "";

  const save = async () => {
    const required = schema.filter((f) => f.required).map((f) => f.key);
    for (const k of required) {
      if (!getVal(k)) { toast.error(`${schema.find((f) => f.key === k)?.label || k} required`); return; }
    }
    const payload = { ...form, asset_type: assetType, site_id: editing ? editing.site_id : (selectedSite && selectedSite !== "all" ? selectedSite : sites[0]?.site_id) };
    if (!payload.site_id) { toast.error("Pick a site first"); return; }
    try {
      if (editing) await api.patch(`/assets/${editing.asset_id}`, payload);
      else await api.post("/assets", payload);
      toast.success(editing ? "Asset updated" : "Asset added");
      setOpen(false); setEditing(null); setForm(emptyForm); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const remove = async (a) => {
    if (!confirm(`Delete ${a.name}? (Soft-delete — historical references preserved.)`)) return;
    await api.delete(`/assets/${a.asset_id}`); toast.success("Asset removed"); load();
  };

  const grouped = items.reduce((acc, a) => { (acc[a.location_label || a.location || "Unspecified"] ??= []).push(a); return acc; }, {});
  const dueSoon = items.filter((a) => a.next_service && a.next_service.slice(0, 10) <= new Date().toISOString().slice(0, 10));

  return (
    <div className="space-y-6" data-testid={`registry-${assetType}-page`}>
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs text-gray-500">Asset Registries → {title}</div>
          <h1 className="text-3xl md:text-4xl font-extrabold flex items-center gap-2">
            {Icon && <Icon className={`w-7 h-7 text-${accentColor}-400`} />} {title}
          </h1>
          <p className="text-sm text-gray-500 mt-1">Asset register driving its module page · org-scoped · audit-trailed</p>
        </div>
        <Button onClick={() => { setEditing(null); setForm(emptyForm); setOpen(true); }} className="f360-gradient border-0" data-testid={`add-${assetType}-btn`}>
          <Plus className="w-4 h-4 mr-1" strokeWidth={2.5} /> Add {title.replace(/s$/, "")}
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Total" value={items.length} />
        <Kpi label="Active" value={items.filter((a) => a.status === "active").length} tone="green" />
        <Kpi label="Fault / Offline" value={items.filter((a) => a.status === "fault" || a.status === "offline").length} tone="red" />
        <Kpi label="Service Due" value={dueSoon.length} tone="amber" />
      </div>

      {Object.entries(grouped).map(([loc, list]) => (
        <section key={loc}>
          <div className="text-xs uppercase tracking-widest text-gray-500 font-bold mb-2 flex items-center gap-2"><Building2 className="w-3.5 h-3.5" strokeWidth={2.5} /> {loc} <span className="text-gray-500">· {list.length}</span></div>
          <div className="glass rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-500">
                <tr><th className="text-left p-3">Name</th><th className="text-left">Make / Model</th><th className="text-left">System</th><th className="text-left">Status</th><th className="text-left">Last Tested</th><th className="text-left">Next Service</th><th></th></tr>
              </thead>
              <tbody>
                {list.map((a) => (
                  <tr key={a.asset_id} className="border-t border-gray-200 hover:bg-gray-50" data-testid={`asset-row-${a.asset_id}`}>
                    <td className="p-3"><div className="font-bold text-gray-900">{a.name}</div><div className="text-[10px] text-gray-500">{a.asset_id}</div></td>
                    <td className="text-xs">{a.make || "—"}{a.model && <> · {a.model}</>}</td>
                    <td className="text-xs uppercase">{a.system || "—"}</td>
                    <td><Badge className={`text-[10px] ${a.status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"}`}>{a.status}</Badge></td>
                    <td className="text-xs text-gray-500 tabular-nums">{a.last_tested?.slice(0, 10) || "—"}</td>
                    <td className={`text-xs tabular-nums ${a.next_service && a.next_service.slice(0, 10) <= new Date().toISOString().slice(0, 10) ? "text-red-700" : "text-gray-500"}`}>{a.next_service?.slice(0, 10) || "—"}</td>
                    <td className="text-right pr-3"><div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setEditing(a); setForm({ ...emptyForm, ...a }); setOpen(true); }} data-testid={`edit-${a.asset_id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-700 hover:text-red-600" onClick={() => remove(a)} data-testid={`delete-${a.asset_id}`}><Trash2 className="w-3.5 h-3.5" strokeWidth={2.5} /></Button>
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}
      {!items.length && <div className="glass rounded-lg p-10 text-center text-gray-500 text-sm" data-testid="empty">No {title.toLowerCase()} yet. Click "Add" to register the first one.</div>}

      <Dialog open={open} onOpenChange={(v) => !v && (setOpen(false), setEditing(null))}>
        <DialogContent className="bg-white border border-gray-200 text-gray-900 max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? `Edit ${title.replace(/s$/, "")}` : `Add New ${title.replace(/s$/, "")}`}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 text-sm">
            {schema.map((f) => {
              // Section heading row (full width)
              if (f.kind === "section") {
                return (
                  <div key={f.key} className="col-span-2 border-t border-gray-200 pt-3 mt-1">
                    <div className="text-[10px] uppercase tracking-widest text-orange-600 font-bold">{f.label}</div>
                    {f.hint && <div className="text-[10px] text-gray-500 mt-0.5">{f.hint}</div>}
                  </div>
                );
              }
              // Computed read-only field
              if (f.kind === "computed") {
                const val = (() => { try { return f.compute ? f.compute(form) : ""; } catch { return ""; } })();
                return (
                  <label key={f.key} className={`flex flex-col gap-1 ${f.full ? "col-span-2" : ""}`}>
                    <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">{f.label}</span>
                    <div className="bg-gray-50 border border-gray-200 rounded px-3 py-2 text-sm text-orange-600 font-bold" data-testid={`computed-${f.key.replace(".", "-")}`}>{val || "—"}</div>
                  </label>
                );
              }
              return (
                <label key={f.key} className={`flex flex-col gap-1 ${f.full ? "col-span-2" : ""}`}>
                  <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">{f.label}{f.required && " *"}</span>
                  {f.kind === "select" ? (
                    <Select value={String(getVal(f.key))} onValueChange={(v) => setVal(f.key, v)}>
                      <SelectTrigger className="bg-gray-50 border-gray-200" data-testid={`field-${f.key.replace(".", "-")}`}><SelectValue placeholder={f.placeholder || "Select…"} /></SelectTrigger>
                      <SelectContent className="bg-white border-gray-200 text-gray-900">
                        {(f.options || []).map((o) => <SelectItem key={o.v ?? o} value={String(o.v ?? o)}>{o.label ?? o}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : f.kind === "textarea" ? (
                    <textarea value={getVal(f.key)} onChange={(e) => setVal(f.key, e.target.value)} className="bg-gray-50 border border-gray-200 rounded px-2 py-1 min-h-[60px]" data-testid={`field-${f.key.replace(".", "-")}`} />
                  ) : (
                    <Input type={f.kind === "number" ? "number" : f.kind === "date" ? "date" : "text"} step={f.step} value={getVal(f.key)} onChange={(e) => setVal(f.key, e.target.value)} placeholder={f.placeholder} className="bg-gray-50 border-gray-200" data-testid={`field-${f.key.replace(".", "-")}`} />
                  )}
                </label>
              );
            })}

            <div className="col-span-2 border-t border-gray-200 pt-3 mt-1">
              <div className="text-[10px] uppercase tracking-widest text-orange-600 font-bold">Live Data Source (optional)</div>
              <div className="text-[10px] text-gray-500 mt-0.5">Link this asset to a gateway register to show live readings instead of relying on manual testing only.</div>
            </div>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Gateway</span>
              <Select value={form.gateway_id || "_none"} onValueChange={(v) => setForm((f) => ({ ...f, gateway_id: v === "_none" ? "" : v, io_point_name: "" }))}>
                <SelectTrigger className="bg-gray-50 border-gray-200" data-testid="field-gateway_id"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent className="bg-white border-gray-200 text-gray-900">
                  <SelectItem value="_none">None</SelectItem>
                  {gateways.map((g) => <SelectItem key={g.gateway_id} value={g.gateway_id}>{g.gateway_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">IO Point</span>
              <Select value={form.io_point_name || "_none"} onValueChange={(v) => setForm((f) => ({ ...f, io_point_name: v === "_none" ? "" : v }))} disabled={!form.gateway_id}>
                <SelectTrigger className="bg-gray-50 border-gray-200" data-testid="field-io_point_name"><SelectValue placeholder={form.gateway_id ? "Select…" : "Select a gateway first"} /></SelectTrigger>
                <SelectContent className="bg-white border-gray-200 text-gray-900">
                  <SelectItem value="_none">None</SelectItem>
                  {ioOptions.map((m) => <SelectItem key={m.mapping_id} value={m.io_point_name}>{m.io_point_name} {m.engineering_unit ? `(${m.engineering_unit})` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </label>
            {form.gateway_id && form.io_point_name && (
              <div className="col-span-2 text-xs">
                {liveValue?.value != null ? (
                  <span className="text-emerald-700 font-bold">Live: {liveValue.value} {liveValue.unit || ""} · {new Date(liveValue.ingested_at).toLocaleTimeString()}</span>
                ) : editing ? (
                  <span className="text-gray-500">No live data yet for this register</span>
                ) : (
                  <span className="text-gray-500">Save the asset to preview its live value</span>
                )}
              </div>
            )}

            {assetType === "fire_alarm_device" && (
              <>
                <div className="col-span-2 border-t border-gray-200 pt-3 mt-1">
                  <div className="text-[10px] uppercase tracking-widest text-orange-600 font-bold">Linked Addressable Point (optional)</div>
                  <div className="text-[10px] text-gray-500 mt-0.5">Shows this point's real alarm/fault status on the Detection page instead of relying only on live register data.</div>
                </div>
                <label className="flex flex-col gap-1 col-span-2">
                  <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Addressable Point</span>
                  <Select value={form.linked_point_id || "_none"} onValueChange={(v) => setForm((f) => ({ ...f, linked_point_id: v === "_none" ? "" : v }))}>
                    <SelectTrigger className="bg-gray-50 border-gray-200" data-testid="field-linked_point_id"><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent className="bg-white border-gray-200 text-gray-900">
                      <SelectItem value="_none">None</SelectItem>
                      {points.map((p) => (
                        <SelectItem key={p.point_id} value={p.point_id}>
                          {p.point_type} — Loop {p.loop_number} / Addr {p.address} {p.zone ? `(${p.zone})` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </label>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpen(false); setEditing(null); }} className="border-gray-300">Cancel</Button>
            <Button onClick={save} className="f360-gradient border-0" data-testid={`save-${assetType}-btn`}>{editing ? "Save Changes" : "Add to Registry"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kpi({ label, value, tone = "orange" }) {
  const grad = { orange: "from-orange-500 to-pink-500", green: "from-emerald-400 to-emerald-600", red: "from-red-500 to-pink-600", amber: "from-amber-400 to-orange-500" };
  return (
    <div className="glass rounded-lg p-4">
      <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${grad[tone]} flex items-center justify-center mb-2`}><ClipboardList className="w-5 h-5 text-gray-900" strokeWidth={2.5} /></div>
      <div className="text-2xl font-extrabold">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-gray-500 mt-1">{label}</div>
    </div>
  );
}
